import { useState, useCallback } from 'react'
import {
    injectTourStyles,
    cleanupTourElements,
    createTourPopover,
    positionPopover,
    createTourOverlay,
    TourDemoType,
} from '@/utils/tour-styles'

interface TourStep {
    selector: string
    title: string
    description: string
    demo?: TourDemoType
}

const TOUR_STEPS: TourStep[] = [
    // Step 1: Select invoices to merge
    {
        selector:
            '[data-tour="invoice-table"] tbody tr:first-child input[type="checkbox"]',
        title: 'Chọn Hóa Đơn Cần Gộp',
        description:
            'Click vào checkbox để chọn các hóa đơn muốn gộp:\n• Chỉ có thể gộp hóa đơn ở trạng thái "Chờ Xác Nhận" hoặc "Đã Duyệt"\n• Phải chọn ít nhất 2 hóa đơn\n• Các hóa đơn đã chọn sẽ được đánh dấu màu xanh',
    },
    // Step 2: View selection summary
    {
        selector: '[data-tour="invoice-table"] [data-tour="selection-summary"]',
        title: 'Tổng Hợp Hóa Đơn Đã Chọn',
        description:
            'Thanh trạng thái hiển thị:\n• Số lượng hóa đơn đã chọn\n• Tổng giá trị các hóa đơn\n• Nút "Gộp Hóa Đơn" để thực hiện gộp',
        demo: 'invoice-selection-summary',
    },
    // Step 3: Merge button
    {
        selector:
            '[data-tour="invoice-table"] button:has([data-testid="MergeIcon"])',
        title: 'Nút Gộp Hóa Đơn',
        description:
            'Nhấn nút "Gộp Hóa Đơn" để:\n• Xem preview các dịch vụ sẽ được gộp\n• Kiểm tra tổng tiền\n• Xác nhận thao tác gộp\n\nLưu ý: Nút chỉ active khi đã chọn ít nhất 2 hóa đơn hợp lệ.',
        demo: 'merge-button-click',
    },
    // Step 4: Merge preview dialog
    {
        selector: '[role="dialog"]',
        title: 'Xác Nhận Gộp Hóa Đơn',
        description:
            'Modal xác nhận hiển thị:\n\n**Preview Dịch Vụ:**\n• Danh sách tất cả dịch vụ từ các hóa đơn được chọn\n• Kỳ áp dụng của từng dịch vụ\n• Đơn giá và thành tiền\n\n**Tổng Cộng:**\n• Tổng giá trị hóa đơn sau gộp\n\n**Xác Nhận:**\n• Nhấn "Xác Nhận" để hoàn tất gộp\n• Nhấn "Hủy" để quay lại',
        demo: 'merge-preview-modal',
    },
    // Step 5: Confirm merge action
    {
        selector: '[role="dialog"] button[type="submit"]',
        title: 'Xác Nhận Thực Hiện',
        description:
            'Nhấn nút "Xác Nhận" để:\n• Gộp các hóa đơn đã chọn thành một hóa đơn duy nhất\n• Các hóa đơn cũ sẽ bị xóa\n• Hóa đơn mới sẽ chứa tất cả dịch vụ từ các hóa đơn đã gộp\n\n⚠️ **Lưu ý:** Thao tác này không thể hoàn tác!',
    },
]

/**
 * Tour hook for Invoice Merge functionality
 * Guides users through selecting and merging multiple invoices
 */
export const useMergeInvoicesTour = () => {
    const [isActive, setIsActive] = useState(false)
    const [currentStep, setCurrentStep] = useState(0)

    const getVisibleSteps = useCallback(() => {
        return TOUR_STEPS.filter((step) => {
            const element = document.querySelector(step.selector)
            return element !== null
        })
    }, [])

    const endTour = useCallback(() => {
        cleanupTourElements()
        setIsActive(false)
        setCurrentStep(0)
    }, [])

    const showTourStep = useCallback(
        (stepIndex: number, steps: TourStep[]) => {
            injectTourStyles()
            cleanupTourElements()
            createTourOverlay()

            const step = steps[stepIndex]
            if (!step) return

            const element = document.querySelector(step.selector) as HTMLElement
            if (!element) {
                // Skip to next step if element not found
                if (stepIndex < steps.length - 1) {
                    showTourStep(stepIndex + 1, steps)
                } else {
                    endTour()
                }
                return
            }

            element.classList.add('tour-highlight-element')
            element.scrollIntoView({ behavior: 'smooth', block: 'center' })

            setTimeout(() => {
                const isLastStep = stepIndex === steps.length - 1
                const popover = createTourPopover({
                    title: step.title,
                    description: step.description,
                    currentStep: stepIndex + 1,
                    totalSteps: steps.length,
                    onPrev:
                        stepIndex > 0
                            ? () => goToStep(stepIndex - 1, steps)
                            : undefined,
                    onNext: isLastStep
                        ? endTour
                        : () => goToStep(stepIndex + 1, steps),
                    onClose: endTour,
                    isLastStep,
                    demo: step.demo,
                })

                document.body.appendChild(popover)
                positionPopover(popover, element)
            }, 300)
        },
        [endTour]
    )

    const goToStep = useCallback(
        (stepIndex: number, steps: TourStep[]) => {
            setCurrentStep(stepIndex)
            showTourStep(stepIndex, steps)
        },
        [showTourStep]
    )

    const startTour = useCallback(() => {
        const visibleSteps = getVisibleSteps()
        if (visibleSteps.length === 0) {
            console.warn('[Merge Invoices Tour] No visible elements to tour')
            return
        }

        setIsActive(true)
        setCurrentStep(0)
        showTourStep(0, visibleSteps)
    }, [getVisibleSteps, showTourStep])

    return {
        isActive,
        currentStep,
        startTour,
        endTour,
        totalSteps: getVisibleSteps().length,
    }
}

export default useMergeInvoicesTour
